import React from 'react';
import { Box, Typography, Alert, Paper, Skeleton, TextField, useMediaQuery, useTheme } from '@mui/material';
import { AppDialog, ConfirmDialog } from '../components/dialog';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import GridLayout, { WidthProvider, Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { buildApiUrl } from '../config';
import {
  Chart,
  DataFreshness,
  type FreshnessStatus,
  DownloadButton,
  EmptyState,
  MetaFacts,
  MetricCard,
  PageHeader,
  Panel,
  ResultTable,
  SidePanelDock,
  ToolbarButton,
  formatMetricValue,
  type ActionMenuItem,
} from '../ui-kit';
import { buildResultColumns } from '../components/table/resultColumns';

// Icon images are configurable via /public/icons/*.svg; replace files to change visuals
import { formatRelativeTime, formatAbsolute, parseBackendDate } from '../utils/date';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import ChatRounded from '@mui/icons-material/ChatRounded';
import OnboardingCoachmark from '../components/OnboardingCoachmark';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { downloadChartImage, type ChartImageFormat } from '../utils/chartDownload';
import { downloadFrame, fetchFramePage } from '../utils/frameExport';
import { markProductStep, useProductOnboarding } from '../hooks/useProductOnboarding';
import { useWideBoard } from './DashboardRoute';
import { createSurfaceChatStore } from './surface-chat/createSurfaceChatStore';
import { useSurfaceChat } from './surface-chat/useSurfaceChat';
import DashboardChatPanel from './dashboard-chat/DashboardChatPanel';
import { buildDashboardSurfaceContext } from './dashboard-chat/buildDashboardSurfaceContext';
import { hydrateArtifactByRef, getArtifactHydrationState } from './copilot/hooks/useArtifactHydration';
import { trackWidgetAdded } from '../utils/analytics';
import type {
  ChartArtifact,
  MetricArtifact,
  QueryResultTab,
  WorkspaceArtifact,
} from './copilot/stores/useCopilotStore';

const Grid = WidthProvider(GridLayout as any);

const ICONS = { drag: '/icons/drag.svg' };

const CHAT_WIDTH_KEY = 'klaris_dashboard_chat_width';

// One store per dashboard, created outside the component so the conversation
// survives re-renders and two dashboards never share a thread.
const chatStores = new Map<string, ReturnType<typeof createSurfaceChatStore>>();
function chatStoreFor(dashboardId: string, name: string) {
  let store = chatStores.get(dashboardId);
  if (!store) {
    store = createSurfaceChatStore(name);
    chatStores.set(dashboardId, store);
  }
  return store;
}

function IconImg({ src, alt }: { src: string; alt?: string }) {
  return <img src={src} alt={alt || ''} style={{ width: 18, height: 18, display: 'block' }} />;
}

type Widget = {
  widget_id: string;
  title: string;
  assistant_message_id: string;
  propose_call_id?: string | null;
  workflow_id?: string;  // Chat V2
  chart_event_id?: string;  // Chat V2
  pos_x: number;
  pos_y: number;
  width: number;
  height: number;
  created_at: string;
  rendered_at?: string | null;
  render_payload?: any;
};

export default function DashboardDetailPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrowCoach = useMediaQuery(theme.breakpoints.down('sm'));
  const { notify } = React.useContext(SnackbarContext);
  const { shouldShow: onboardingActive, currentStepId, loading: onboardingLoading } = useProductOnboarding();
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const location = useLocation();
  
  // Try to get dashboard metadata from navigation state first (passed from listing page)
  const passedState = location.state as { dashboardName?: string; dashboardDescription?: string; dashboardId?: string } | null;
  const [dashboardName, setDashboardName] = React.useState<string>(passedState?.dashboardName || 'Dashboard');
  const [dashboardDescription, setDashboardDescription] = React.useState<string | null>(passedState?.dashboardDescription || null);
  const [widgets, setWidgets] = React.useState<Widget[]>([]);
  const [specs, setSpecs] = React.useState<Record<string, any>>({});
  const [widgetErrors, setWidgetErrors] = React.useState<Record<string, string | null>>({});
  const [widgetLoading, setWidgetLoading] = React.useState<Record<string, boolean>>({});
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [resizing, setResizing] = React.useState<{ id: string; w: number; h: number } | null>(null);
  const [refreshedAt, setRefreshedAt] = React.useState<Record<string, string>>({});
  const [inlineEditId, setInlineEditId] = React.useState<string | null>(null);
  const [inlineEditValue, setInlineEditValue] = React.useState<string>('');
  const [addWidgetOpen, setAddWidgetOpen] = React.useState(false);
  const [addWidgetQuestion, setAddWidgetQuestion] = React.useState('');
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = React.useState(false);
  // Bumped when the chat pins something, so the new widget appears without a
  // page reload.
  const [reloadNonce, setReloadNonce] = React.useState(0);
  // Which widgets the questions are about. The chat belongs to the dashboard;
  // adding widgets only changes what an unqualified question means, and the
  // rest of the dashboard stays in context so comparisons still work.
  const [selectedWidgetIds, setSelectedWidgetIds] = React.useState<string[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteWidgetId, setDeleteWidgetId] = React.useState<string | null>(null);
  const chartViewRefs = React.useRef<Record<string, React.RefObject<any>>>({});
  const [dashboardLastUpdated, setDashboardLastUpdated] = React.useState<string | null>(null);
  const [isRefreshingAll, setIsRefreshingAll] = React.useState(false);
  const [refreshAllProgress, setRefreshAllProgress] = React.useState({ done: 0, total: 0, succeeded: 0, failed: 0 });
  const refreshAllCancelRef = React.useRef(false);
  const [showEditNudge, setShowEditNudge] = React.useState(false);
  const editAgentBtnRef = React.useRef<any>(null);
  const closeAddWidgetDialog = React.useCallback(() => { setAddWidgetOpen(false); setAddWidgetQuestion(''); }, []);
  const closeDeleteConfirmDialog = React.useCallback(() => { setDeleteConfirmOpen(false); setDeleteWidgetId(null); }, []);

  const chatStore = chatStoreFor(String(dashboardId || 'unknown'), dashboardName);
  // Read through a ref so the send callbacks keep their identity while the
  // dashboard underneath them changes.
  const surfaceContextRef = React.useRef<Record<string, unknown> | null>(null);
  const getSurfaceContext = React.useCallback(() => surfaceContextRef.current, []);
  const { sendMessage, sendSQLApproval, stopStream } = useSurfaceChat({
    useStore: chatStore,
    dashboardId: String(dashboardId || ''),
    getSurfaceContext,
    enabled: chatOpen,
  });
  // A full-bleed grid of widgets is a wide board whether or not the chat is
  // open, and the layout pads a normal page twice as far from the breadcrumb.
  // Scrolling only moves to the page once the chat docks beside it.
  useWideBoard(true, chatOpen);

  const tenantId = React.useMemo(() => {
    try { const t = JSON.parse(String(window.localStorage.getItem('klaris_tenant'))); return String(t?.tenant_id || ''); } catch { return ''; }
  }, []);

  // Helper to format snake_case titles to Title Case
  const toTitleCase = React.useCallback((str: string): string => {
    // If already has spaces or uppercase letters, return as-is (user customized)
    if (str.includes(' ') || /[A-Z]/.test(str)) {
      return str;
    }
    // Otherwise format snake_case
    return str
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, []);

  // Helper to get widget title - format snake_case titles
  const getWidgetDisplayTitle = React.useCallback((widget: Widget): string => {
    return toTitleCase(widget.title);
  }, [toTitleCase]);

  /**
   * Point the chat at a widget.
   *
   * The summary is sent for you: an empty composer beside a chart you just
   * asked about is a worse start than a paragraph saying what it shows. Held
   * back while a turn is running, though — the composer is frozen then, and a
   * second turn arriving unbidden would be a surprise rather than a help.
   */
  const addWidgetToChat = React.useCallback(
    (w: Widget, markOnboarding: boolean) => {
      const already = selectedWidgetIds.includes(w.widget_id);
      setChatOpen(true);
      if (!already) setSelectedWidgetIds((ids) => [...ids, w.widget_id]);
      if (markOnboarding) {
        void markProductStep('widget_summarized', { dashboard_id: String(dashboardId || '') });
      }
      if (!already && !chatStore.getState().isStreaming) {
        void sendMessage(
          `Summarise the key insights, trends and notable patterns in "${getWidgetDisplayTitle(w)}".`,
        );
      }
    },
    [selectedWidgetIds, chatStore, dashboardId, sendMessage, getWidgetDisplayTitle],
  );

  const startInlineEdit = React.useCallback((w: Widget) => {
    setInlineEditId(w.widget_id);
    setInlineEditValue(toTitleCase(w.title));
  }, [toTitleCase]);

  const saveInlineTitle = React.useCallback(async (widgetId: string) => {
    if (!tenantId || !dashboardId || !inlineEditValue.trim()) {
      setInlineEditId(null);
      return;
    }
    try {
      const token = window.localStorage.getItem('klaris_jwt');
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(widgetId)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ title: inlineEditValue.trim() })
        } as RequestInit
      );
      if (resp.ok) {
        setWidgets(prev => prev.map(w =>
          w.widget_id === widgetId ? { ...w, title: inlineEditValue.trim() } : w
        ));
      }
    } catch {}
    setInlineEditId(null);
  }, [tenantId, dashboardId, inlineEditValue]);

  // Fetch individual widget data from database (no re-execution)
  // Falls back to synchronous /render if render_payload is not yet available
  // (background task may still be running after widget creation)
  const fetchWidgetData = React.useCallback(async (widgetId: string) => {
    if (!tenantId || !dashboardId) return;
    const token = window.localStorage.getItem('klaris_jwt');
    setWidgetLoading(prev => ({ ...prev, [widgetId]: true }));
    setWidgetErrors(prev => ({ ...prev, [widgetId]: null }));
    
    try {
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(widgetId))}`),
        { headers: { Authorization: `Bearer ${token}` } } as RequestInit
      );
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data?.detail || data?.error || 'Failed to load widget');
      }
      
      // Extract render_payload from widget response
      const renderPayload = data?.render_payload;
      if (renderPayload && (renderPayload.spec || renderPayload.type)) {
        setSpecs(prev => ({ ...prev, [widgetId]: renderPayload }));
        if (data.rendered_at) {
          setRefreshedAt(prev => ({ ...prev, [widgetId]: data.rendered_at }));
        }
      } else {
        // render_payload not yet available (background task may still be running)
        // Fall back to synchronous /render endpoint
        const renderResp = await fetch(
          buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(widgetId))}/render`),
          { headers: { Authorization: `Bearer ${token}` } } as RequestInit
        );
        const renderData = await renderResp.json();
        
        if (!renderResp.ok) {
          throw new Error(renderData?.detail || renderData?.error || 'Failed to render widget');
        }
        
        if (renderData?.spec || renderData?.type) {
          setSpecs(prev => ({ ...prev, [widgetId]: renderData }));
          // Don't set refreshedAt here - this is a non-force render (initial load fallback),
          // not an actual data refresh. rendered_at from the server is the source of truth.
        }
      }
    } catch (e: any) {
      setWidgetErrors(prev => ({ ...prev, [widgetId]: e?.message || 'Failed to load widget' }));
    } finally {
      setWidgetLoading(prev => ({ ...prev, [widgetId]: false }));
    }
  }, [tenantId, dashboardId]);

  // Refresh widget by re-executing (calls /render endpoint)
  const fetchWidget = React.useCallback(async (w: Widget) => {
    const token = window.localStorage.getItem('klaris_jwt');
    setWidgetLoading(prev => ({ ...prev, [w.widget_id]: true }));
    setWidgetErrors(prev => ({ ...prev, [w.widget_id]: null }));
    
    try {
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(w.widget_id))}/render?force=true`),
        { headers: { Authorization: `Bearer ${token}` } } as RequestInit
      );
      const data = await resp.json();
      
      if (!resp.ok) {
        throw new Error(data?.detail || data?.error || 'Failed to refresh widget');
      }
      
      // Store entire response for all widget types
      if (data?.spec || data?.type) {
        setSpecs(prev => ({ ...prev, [w.widget_id]: data }));
        // Re-fetch widget to get the real server-side rendered_at
        // (only set by backend when replay actually re-executed queries)
        try {
          const wResp = await fetch(
            buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(w.widget_id))}`),
            { headers: { Authorization: `Bearer ${token}` } } as RequestInit
          );
          const wData = await wResp.json();
          if (wResp.ok && wData?.rendered_at) {
            setRefreshedAt(prev => ({ ...prev, [w.widget_id]: wData.rendered_at }));
            setDashboardLastUpdated(wData.rendered_at);
            setWidgets(prev => prev.map(ww =>
              ww.widget_id === w.widget_id ? { ...ww, render_payload: data, rendered_at: wData.rendered_at } : ww
            ));
          } else {
            setWidgets(prev => prev.map(ww =>
              ww.widget_id === w.widget_id ? { ...ww, render_payload: data } : ww
            ));
          }
        } catch {
          // If re-fetch fails, just update the render_payload without touching rendered_at
          setWidgets(prev => prev.map(ww =>
            ww.widget_id === w.widget_id ? { ...ww, render_payload: data } : ww
          ));
        }
      }
    } catch (e: any) {
      setWidgetErrors(prev => ({ ...prev, [w.widget_id]: e?.message || 'Failed to refresh widget' }));
    } finally {
      setWidgetLoading(prev => ({ ...prev, [w.widget_id]: false }));
    }
  }, [tenantId, dashboardId]);

  const deleteWidget = React.useCallback(async (id: string) => {
    if (!tenantId || !dashboardId) return;
    const token = window.localStorage.getItem('klaris_jwt');
    try {
      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(id)}`), {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      } as RequestInit);
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j?.detail || j?.error || 'Failed to delete');
      }
      setWidgets(prev => prev.filter(w => w.widget_id !== id));
      setSpecs(prev => { const copy = { ...prev }; delete (copy as any)[id]; return copy; });
      setWidgetErrors(prev => { const copy = { ...prev }; delete (copy as any)[id]; return copy; });
    } catch (e) {
      // Surface an alert at top-level for now
      setError((e as any)?.message || 'Failed to delete widget');
    }
  }, [tenantId, dashboardId]);

  const refreshAllWidgets = React.useCallback(async () => {
    if (!tenantId || !dashboardId || widgets.length === 0) return;
    const token = window.localStorage.getItem('klaris_jwt');
    refreshAllCancelRef.current = false;
    setIsRefreshingAll(true);
    setRefreshAllProgress({ done: 0, total: widgets.length, succeeded: 0, failed: 0 });

    const CONCURRENCY = 3;
    let succeeded = 0;
    let failed = 0;
    let done = 0;
    const queue = [...widgets];

    const processOne = async () => {
      while (queue.length > 0) {
        if (refreshAllCancelRef.current) return;
        const w = queue.shift()!;
        setWidgetLoading(prev => ({ ...prev, [w.widget_id]: true }));
        setWidgetErrors(prev => ({ ...prev, [w.widget_id]: null }));
        try {
          const resp = await fetch(
            buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(w.widget_id))}/render?force=true`),
            { headers: { Authorization: `Bearer ${token}` } } as RequestInit
          );
          const data = await resp.json();
          if (!resp.ok) throw new Error(data?.detail || data?.error || 'Failed to refresh');
          if (data?.spec || data?.type) {
            setSpecs(prev => ({ ...prev, [w.widget_id]: data }));
            // Re-fetch widget to get real server-side rendered_at
            try {
              const wResp = await fetch(
                buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/${encodeURIComponent(String(w.widget_id))}`),
                { headers: { Authorization: `Bearer ${token}` } } as RequestInit
              );
              const wData = await wResp.json();
              if (wResp.ok && wData?.rendered_at) {
                setRefreshedAt(prev => ({ ...prev, [w.widget_id]: wData.rendered_at }));
                setWidgets(prev => prev.map(ww =>
                  ww.widget_id === w.widget_id ? { ...ww, render_payload: data, rendered_at: wData.rendered_at } : ww
                ));
              } else {
                setWidgets(prev => prev.map(ww =>
                  ww.widget_id === w.widget_id ? { ...ww, render_payload: data } : ww
                ));
              }
            } catch {
              setWidgets(prev => prev.map(ww =>
                ww.widget_id === w.widget_id ? { ...ww, render_payload: data } : ww
              ));
            }
          }
          succeeded++;
        } catch (e: any) {
          setWidgetErrors(prev => ({ ...prev, [w.widget_id]: e?.message || 'Refresh failed' }));
          failed++;
        } finally {
          setWidgetLoading(prev => ({ ...prev, [w.widget_id]: false }));
          done++;
          setRefreshAllProgress({ done, total: widgets.length, succeeded, failed });
        }
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, widgets.length) }, () => processOne());
    await Promise.all(workers);

    setIsRefreshingAll(false);
    const now = new Date().toISOString();
    setDashboardLastUpdated(now);
    if (refreshAllCancelRef.current) {
      notify(`Refresh cancelled - ${succeeded} updated, ${failed} failed`, 'info');
    } else if (failed > 0) {
      notify(`Refreshed ${succeeded}/${widgets.length} widgets (${failed} failed)`, 'warning');
    } else {
      notify(`All ${succeeded} widgets refreshed successfully`, 'success');
    }
  }, [tenantId, dashboardId, widgets, notify]);

  const cancelRefreshAll = React.useCallback(() => {
    refreshAllCancelRef.current = true;
  }, []);

  React.useEffect(() => {
    if (!tenantId || !dashboardId) return;
    (async () => {
      setLoading(true); setError(null);
      try {
        const token = window.localStorage.getItem('klaris_jwt');
        // Fetch dashboard metadata and widget list (without render_payload)
        const resp = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets`), {
          headers: { Authorization: `Bearer ${token}` }
        } as RequestInit);
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.detail || data?.error || 'Failed to load widgets');
        
        // Extract dashboard metadata from response (only if not already passed via route state)
        if (data?.dashboard && !passedState?.dashboardName) {
          if (data.dashboard.name) setDashboardName(String(data.dashboard.name));
          setDashboardDescription(data.dashboard.description || null);
        }
        
        const list: Widget[] = Array.isArray(data?.widgets) ? data.widgets : [];
        // Order widgets so newly added (without saved positions) appear last.
        // 1) Keep items with saved positions first (visual layout already defined)
        // 2) Append items without positions, sorted by created_at ascending (newest last)
        const positioned = list.filter((w: any) => Number.isFinite(w?.pos_x) && Number.isFinite(w?.pos_y));
        const unpositioned = list.filter((w: any) => !Number.isFinite(w?.pos_x) || !Number.isFinite(w?.pos_y));
        unpositioned.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        const ordered = [...positioned, ...unpositioned];
        setWidgets(ordered);
        
        // Compute dashboard-level last updated from the most recent widget
        // rendered_at. Use parseBackendDate (not the bare `new Date()`)
        // because FastAPI emits naive ISO strings without a `Z` suffix —
        // ES2015 then interprets them as *local* time, which silently shifts
        // the value by the viewer's UTC offset (this surfaced as
        // "Updated 6 hours ago" for IST users on a freshly rendered board).
        const renderedAts = list
          .map((w: any) => w.rendered_at)
          .filter(Boolean)
          .map((d: string) => parseBackendDate(d)?.getTime())
          .filter((t: number | undefined): t is number => typeof t === 'number' && !isNaN(t));
        if (renderedAts.length > 0) {
          setDashboardLastUpdated(new Date(Math.max(...renderedAts)).toISOString());
        }
        
        // Initialize loading states for all widgets
        const initialLoading: Record<string, boolean> = {};
        const initialErrors: Record<string, string | null> = {};
        for (const w of list) {
          initialLoading[w.widget_id] = true; // Start with loading state
          initialErrors[w.widget_id] = null;
        }
        setWidgetLoading(initialLoading);
        setWidgetErrors(initialErrors);
        setSpecs({});
        setRefreshedAt({});
        
        // Fetch each widget's render data individually for progressive loading
        // This allows fast widgets to render immediately while slow ones show shimmer
        for (const w of list) {
          fetchWidgetData(w.widget_id);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, dashboardId, fetchWidgetData, reloadNonce]);

  const layout = React.useMemo(() => (
    widgets.map((w, idx) => {
      const defaultX = (idx * 4) % 12;
      const defaultY = Math.floor((idx * 4) / 12) * 3;
      const x = (w.pos_x ?? defaultX);
      const y = (w.pos_y ?? defaultY);
      const width = (w.width ?? 4);
      const height = (w.height ?? 3);
      return { i: w.widget_id, x, y, w: width, h: height, resizeHandles: ['se'] } as any;
    })
  ), [widgets]);

  /** Single-column stack for narrow viewports — preserves reading order from saved grid positions */
  const mobileStackLayout = React.useMemo(() => {
    const base = widgets.map((w, idx) => {
      const defaultX = (idx * 4) % 12;
      const defaultY = Math.floor((idx * 4) / 12) * 3;
      const x = (w.pos_x ?? defaultX);
      const y = (w.pos_y ?? defaultY);
      const width = (w.width ?? 4);
      const height = (w.height ?? 3);
      return { i: w.widget_id, x, y, w: width, h: height };
    }).sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
    let yAcc = 0;
    return base.map((l) => {
      const item = { i: l.i, x: 0, y: yAcc, w: 1, h: l.h } as Layout;
      yAcc += l.h;
      return item;
    });
  }, [widgets]);

  const gridLayout = isMobile ? mobileStackLayout : layout;

  const saveTimer = React.useRef<number | null>(null);
  const onLayoutChange = React.useCallback((current: Layout[]) => {
    try {
      if (!tenantId || !dashboardId) return;
      const token = window.localStorage.getItem('klaris_jwt');
      const body = { items: current.map(l => ({ widget_id: String(l.i), x: l.x, y: l.y, w: l.w, h: l.h })) };
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(async () => {
        await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/dashboards/${encodeURIComponent(String(dashboardId))}/widgets/layout`), {
          method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body)
        } as RequestInit);
      }, 400);
    } catch {}
  }, [tenantId, dashboardId]);

  const onLayoutChangeSafe = React.useCallback(
    (current: Layout[]) => {
      if (!isMobile) onLayoutChange(current);
    },
    [isMobile, onLayoutChange],
  );

  const confirmDelete = React.useCallback(async () => {
    if (deleteWidgetId) {
      await deleteWidget(deleteWidgetId);
      closeDeleteConfirmDialog();
    }
  }, [deleteWidgetId, deleteWidget, closeDeleteConfirmDialog]);
  // Charts are all ECharts now, so there is one code path: the instance is
  // read from the ref at call time, not render time, because `onReady`
  // mutates the ref without re-rendering us.
  const downloadWidgetAs = React.useCallback(
    (id: string, format: ChartImageFormat) => {
      const inst = chartViewRefs.current[id]?.current;
      if (!inst) return;
      const widget = widgets.find((x) => x.widget_id === id);
      try {
        downloadChartImage(inst, format, widget ? getWidgetDisplayTitle(widget) : undefined);
      } catch (err: any) {
        notify(err?.message || 'Could not download the chart.', 'error');
      }
    },
    [widgets, notify, getWidgetDisplayTitle],
  );

  // Table widgets export the full dataset straight from the DuckDB frame
  // (same server-side path as the Copilot chat), not just the saved preview.
  const downloadTableAs = React.useCallback(
    async (id: string, format: 'csv' | 'xlsx') => {
      const spec = specs[id];
      const frameId = spec?.data?.frame_id;
      const tId = spec?.metadata?.thread_id;
      if (!frameId || !tId) {
        notify('No data available to export', 'info');
        return;
      }
      const widget = widgets.find((x) => x.widget_id === id);
      const baseName = widget ? getWidgetDisplayTitle(widget) : 'table';
      const label = format === 'xlsx' ? 'Excel' : 'CSV';
      // Only surface a "Preparing…" toast if the download is actually slow.
      // Most exports finish in well under a second; showing it immediately makes
      // the info toast flash and get instantly replaced by the success toast.
      const prepTimer = setTimeout(() => {
        notify(`Preparing ${label} download\u2026`, 'info');
      }, 400);
      try {
        const res = await downloadFrame(frameId, tId, format, baseName);
        clearTimeout(prepTimer);
        notify(`Downloaded ${res.filename}`, 'success');
      } catch (err: any) {
        clearTimeout(prepTimer);
        if (err?.message?.includes('no longer available')) {
          notify('Data expired. Please re-run the query.', 'warning');
        } else {
          notify(err?.message || `Failed to download ${label}`, 'error');
        }
      }
    },
    [specs, widgets, getWidgetDisplayTitle, notify],
  );

  // Listen for dashboard rename events from breadcrumb
  React.useEffect(() => {
    const onRenamed = (ev: Event) => { 
      try { 
        const detail = (ev as CustomEvent).detail;
        if (detail?.name) {
          setDashboardName(detail.name);
        }
      } catch {} 
    };
    window.addEventListener('dashboard:renamed', onRenamed as any);
    return () => window.removeEventListener('dashboard:renamed', onRenamed as any);
  }, []);

  // “Chat with Copilot” coach mark: only during onboarding pin-widget flow (not every add).
  // After pin, backend marks widget_added done so current_step_id is usually widget_summarized next.
  React.useEffect(() => {
    if (onboardingLoading) return;
    try {
      const flagged = localStorage.getItem('klaris_nudge_edit_agent') === '1';
      const inPinWidgetFlow =
        currentStepId === 'widget_added' || currentStepId === 'widget_summarized';
      const showNudge = flagged && onboardingActive && inPinWidgetFlow;
      setShowEditNudge(showNudge);
      if (flagged && !showNudge) {
        localStorage.removeItem('klaris_nudge_edit_agent');
      }
    } catch {
      setShowEditNudge(false);
    }
  }, [onboardingLoading, onboardingActive, currentStepId]);

  /** The header's left side: the drag handle, then the title, editable in place. */
  const widgetTitle = (w: Widget) => (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      {!isMobile && (
        <Box
          component="span"
          className="widget-drag-handle"
          sx={(t) => ({
            display: 'inline-flex',
            flexShrink: 0,
            cursor: 'grab',
            opacity: 0.5,
            transition: `opacity ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
            '&:active': { cursor: 'grabbing' },
            '&:hover': { opacity: 0.9 },
          })}
        >
          <IconImg src={ICONS.drag} alt="drag" />
        </Box>
      )}
      {inlineEditId === w.widget_id ? (
        <Box
          component="input"
          autoFocus
          value={inlineEditValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInlineEditValue(e.target.value)}
          onBlur={() => saveInlineTitle(w.widget_id)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') { e.preventDefault(); saveInlineTitle(w.widget_id); }
            if (e.key === 'Escape') { setInlineEditId(null); }
          }}
          sx={(t) => ({
            ...t.typography.subtitle2,
            width: '100%',
            minWidth: 0,
            px: 0.75,
            py: 0.25,
            color: t.tokens.text.primary,
            bgcolor: 'transparent',
            border: `1px solid ${t.tokens.border.default}`,
            borderRadius: `${t.ds.radius.xs}px`,
            outline: 'none',
            fontFamily: 'inherit',
          })}
        />
      ) : (
        <Box
          component="span"
          onClick={() => startInlineEdit(w)}
          sx={{ cursor: 'text', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {getWidgetDisplayTitle(w)}
        </Box>
      )}
    </Box>
  );

  const refreshAction = (w: Widget) => (
    <ToolbarButton
      variant="ghost"
      icon={<RefreshIcon fontSize="small" />}
      onClick={() => fetchWidget(w)}
      disabled={!!widgetLoading[w.widget_id]}
      aria-label="Refresh widget"
      tooltip={
        refreshedAt[w.widget_id]
          ? `Refresh · Updated ${formatAbsolute(refreshedAt[w.widget_id])}`
          : 'Refresh data'
      }
    />
  );

  /** What this widget can be saved as — nothing, for a single number. */
  const downloadFormats = (w: Widget) => {
    const spec = specs[w.widget_id];
    if (spec?.type === 'echarts') {
      return [
        { key: 'png', label: 'Image (.png)', onDownload: () => downloadWidgetAs(w.widget_id, 'png') },
        { key: 'jpeg', label: 'Photo (.jpeg)', onDownload: () => downloadWidgetAs(w.widget_id, 'jpeg') },
        { key: 'svg', label: 'Vector (.svg)', description: 'Sharp at any size', onDownload: () => downloadWidgetAs(w.widget_id, 'svg') },
      ];
    }
    if (spec?.type === 'table_result') {
      return [
        { key: 'xlsx', label: 'Excel (.xlsx)', description: 'Every row', onDownload: () => downloadTableAs(w.widget_id, 'xlsx') },
        { key: 'csv', label: 'CSV', description: 'Every row', onDownload: () => downloadTableAs(w.widget_id, 'csv') },
      ];
    }
    return [];
  };

  /** On the card: the two used constantly. Everything else is behind the ⋯. */
  const widgetActions = (w: Widget) => {
    const isFirst = showEditNudge && w.widget_id === widgets[0]?.widget_id;
    return (
      <>
        {refreshAction(w)}
        <Box component="span" ref={isFirst ? editAgentBtnRef : undefined} sx={{ display: 'inline-flex' }}>
          <ToolbarButton
            variant="ghost"
            icon={<ChatBubbleOutlineRounded fontSize="small" />}
            disabled={!specs[w.widget_id]}
            aria-label="Chat about this widget"
            tooltip="Chat about this widget"
            onClick={() => {
              if (showEditNudge) {
                setShowEditNudge(false);
                try { localStorage.removeItem('klaris_nudge_edit_agent'); } catch { /* ignore */ }
              }
              if (specs[w.widget_id]) addWidgetToChat(w, isFirst);
            }}
          />
        </Box>
      </>
    );
  };

  /**
   * Full screen: refresh and download, and nothing else. Expand is already
   * done, deleting would close what you are looking at, and the Copilot chat
   * docks beside a widget — there is no beside once it fills the screen.
   */
  const expandedActions = (w: Widget) => {
    const formats = downloadFormats(w);
    return (
      <>
        {refreshAction(w)}
        {formats.length > 0 && <DownloadButton formats={formats} />}
      </>
    );
  };

  /** The rest, behind one ⋯ button: Expand comes from the Panel itself. */
  const widgetMenu = (w: Widget): ActionMenuItem[] => {
    const spec = specs[w.widget_id];
    const out: ActionMenuItem[] = [];

    if (spec?.type === 'echarts') {
      out.push({
        key: 'download',
        label: 'Download',
        icon: <FileDownloadOutlined fontSize="small" />,
        items: [
          { key: 'png', label: 'Image (.png)', onSelect: () => downloadWidgetAs(w.widget_id, 'png') },
          { key: 'jpeg', label: 'Photo (.jpeg)', onSelect: () => downloadWidgetAs(w.widget_id, 'jpeg') },
          { key: 'svg', label: 'Vector (.svg)', description: 'Sharp at any size', onSelect: () => downloadWidgetAs(w.widget_id, 'svg') },
        ],
      });
    }
    if (spec?.type === 'table_result') {
      out.push({
        key: 'download',
        label: 'Download',
        icon: <FileDownloadOutlined fontSize="small" />,
        items: [
          { key: 'xlsx', label: 'Excel (.xlsx)', description: 'Every row', onSelect: () => downloadTableAs(w.widget_id, 'xlsx') },
          { key: 'csv', label: 'CSV', description: 'Every row', onSelect: () => downloadTableAs(w.widget_id, 'csv') },
        ],
      });
    }

    out.push({
      key: 'delete',
      label: 'Delete widget',
      icon: <DeleteOutlineRounded fontSize="small" />,
      onSelect: () => { setDeleteWidgetId(w.widget_id); setDeleteConfirmOpen(true); },
    });
    return out;
  };

  /** What the widget actually shows — the same pieces the chat uses. */
  const widgetBody = (w: Widget) => {
    const spec = specs[w.widget_id];
    const failed = widgetErrors[w.widget_id];
    const title = getWidgetDisplayTitle(w);
    if (failed) return <EmptyState tone="error" message={failed} />;
    if (!spec) return null;
    if (spec.type === 'metric_card') {
      return <MetricCard label={spec.metric_name || title} value={formatMetricValue(spec.metric_value)} size="fill" bare />;
    }
    if (spec.type === 'table_result') {
      return <WidgetTable spec={spec} label={title} />;
    }
    if (spec.type === 'echarts') {
      return (
        <Chart
          option={spec.spec || spec}
          fill
          ariaLabel={title}
          onReady={(c) => { chartViewRefs.current[w.widget_id] = { current: c }; }}
        />
      );
    }
    // Charts saved before the move to ECharts: there is no second renderer
    // any more, and refreshing rebuilds the widget in the current format.
    return (
      <EmptyState
        tone="error"
        message="This chart was saved in a format we no longer draw. Refresh the widget to rebuild it."
      />
    );
  };

  const onResize = React.useCallback((_layout: any, _oldItem: any, newItem: any) => {
    try { setResizing({ id: String(newItem.i), w: Number(newItem.w), h: Number(newItem.h) }); } catch {}
  }, []);
  const onResizeStop = React.useCallback(() => { setResizing(null); }, []);

  // Written every render, so the chat always describes what is on screen now
  // rather than what was there when it was opened.
  surfaceContextRef.current = buildDashboardSurfaceContext({
    dashboardId: String(dashboardId || ''),
    name: dashboardName,
    widgets,
    specs,
    selectedWidgetIds,
    lastUpdated: dashboardLastUpdated,
  });

  // Data pulled before today is stale: these widgets hold whatever their last
  // refresh returned, so a board left open overnight must not read as current.
  const freshness: FreshnessStatus = isRefreshingAll
    ? 'updating'
    : (() => {
        const at = dashboardLastUpdated ? parseBackendDate(dashboardLastUpdated) : null;
        if (!at) return 'fresh';
        const today = new Date();
        const sameDay =
          at.getFullYear() === today.getFullYear() &&
          at.getMonth() === today.getMonth() &&
          at.getDate() === today.getDate();
        return sameDay ? 'fresh' : 'stale';
      })();

  // What the answer is looking at, shown in the composer and removable there.
  const chatChips = selectedWidgetIds
    .map((id) => widgets.find((w) => w.widget_id === id))
    .filter((w): w is Widget => Boolean(w))
    .map((w) => ({ key: w.widget_id, label: getWidgetDisplayTitle(w) }));

  /**
   * Pin a result from the chat onto this dashboard.
   *
   * No picker: the chat is docked to a dashboard, so the only sensible
   * destination is the one on screen. The payload is built exactly as the
   * Copilot page builds it — a widget pinned from here has to be
   * indistinguishable from one pinned there, since they land side by side.
   */
  const pinToThisDashboard = React.useCallback(
    async (artifact: WorkspaceArtifact) => {
      const token = window.localStorage.getItem('klaris_jwt');
      const threadId = chatStore.getState().threadId;
      if (!token || !tenantId || !dashboardId || !threadId) return;

      // Rows and specs arrive lazily. Hydrate from THIS chat's store — the
      // default reads the Copilot page's store, which knows nothing of it.
      let resolved = artifact;
      if (getArtifactHydrationState(resolved) !== 'ready') {
        await hydrateArtifactByRef(resolved.id, chatStore.getState());
        resolved = chatStore.getState().workspaceTabs.find((t) => t.id === resolved.id) || resolved;
      }

      let renderPayload: Record<string, any>;
      if (resolved.type === 'chart') {
        const ca = resolved as ChartArtifact;
        renderPayload = { type: 'echarts', spec: ca.spec, metadata: { thread_id: threadId, event_id: ca.eventId } };
      } else if (resolved.type === 'metric_card') {
        const ma = resolved as MetricArtifact;
        renderPayload = {
          type: 'metric_card',
          metric_name: ma.metricName,
          metric_value: ma.metricValue,
          metadata: { thread_id: threadId, event_id: ma.eventId },
        };
      } else {
        const qt = resolved as QueryResultTab;
        // The real frame id, so the widget can page and download at once and
        // the server resolves the exact event rather than the latest table.
        const frameId = qt.frameId || qt.id;
        renderPayload = {
          type: 'table_result',
          data: {
            columns: qt.columns,
            preview_rows: qt.rows,
            row_count: qt.rowCount,
            frame_id: frameId,
            column_types: qt.columnTypes,
            column_semantics: qt.columnSemantics,
          },
          display_hint: 'table',
          metadata: { thread_id: threadId, artifact_id: qt.id, frame_id: frameId },
        };
      }

      try {
        const resp = await fetch(
          buildApiUrl(`/api/v1/tenants/${tenantId}/chat-v2/copilot/dashboards/${encodeURIComponent(String(dashboardId))}/widgets`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              title: resolved.title || 'Widget',
              thread_id: threadId,
              artifact_id: resolved.id,
              widget_type: resolved.type,
              render_payload: renderPayload,
            }),
          } as RequestInit,
        );
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || 'Could not add that to the dashboard.');
        }
        trackWidgetAdded(String(dashboardId));
        notify(`Added “${resolved.title || 'Widget'}” to this dashboard`, 'success');
        setReloadNonce((n) => n + 1);
      } catch (e: any) {
        notify(e?.message || 'Could not add that to the dashboard.', 'error');
      }
    },
    [chatStore, tenantId, dashboardId, notify],
  );

  const page = (
    <Box sx={{
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      minWidth: 0,
      bgcolor: (theme) => theme.palette.mode === 'light' ? '#F8F9FA' : 'background.default',
      minHeight: '100%',
    }}>
      {!loading && (
        <Box sx={{ mb: 1.75 }}>
          <PageHeader
            ariaLabel="About this dashboard"
            title={dashboardName}
            description={dashboardDescription || undefined}
            progress={
              isRefreshingAll && refreshAllProgress.total > 0
                ? (refreshAllProgress.done / refreshAllProgress.total) * 100
                : undefined
            }
            meta={
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                <MetaFacts
                  facts={[
                    {
                      key: 'widgets',
                      icon: <DashboardIcon />,
                      label: `${widgets.length} widget${widgets.length !== 1 ? 's' : ''}`,
                    },
                    {
                      key: 'refreshing',
                      label: isRefreshingAll
                        ? `Refreshing ${refreshAllProgress.done} of ${refreshAllProgress.total}${
                            refreshAllProgress.failed > 0 ? ` · ${refreshAllProgress.failed} failed` : ''
                          }`
                        : '',
                    },
                  ]}
                />
                {/* Derives staleness from the last refresh; saying "fresh"
                    unconditionally is the failure it exists to prevent. */}
                <DataFreshness updatedAt={dashboardLastUpdated} status={freshness} />
              </Box>
            }
            actions={
              <>
                {isRefreshingAll ? (
                  <ToolbarButton onClick={cancelRefreshAll}>Cancel</ToolbarButton>
                ) : (
                  <ToolbarButton
                    icon={<RefreshIcon />}
                    onClick={refreshAllWidgets}
                    disabled={widgets.length === 0}
                    tooltip="Refresh every widget"
                  >
                    {isMobile ? 'Refresh' : 'Refresh all'}
                  </ToolbarButton>
                )}
                <ToolbarButton
                  icon={<ChatRounded />}
                  onClick={() => setChatOpen((open) => !open)}
                  selected={chatOpen}
                  tooltip={chatOpen ? 'Hide chat' : 'Ask about this dashboard'}
                >
                  {chatOpen ? 'Hide chat' : 'Chat'}
                </ToolbarButton>
                <ToolbarButton
                  variant="brand"
                  icon={<AddIcon />}
                  onClick={() => setAddWidgetOpen(true)}
                  tooltip="Ask a question and pin the answer here"
                >
                  {isMobile ? 'Add' : 'Add widget'}
                </ToolbarButton>
              </>
            }
          />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {/* Loading Skeleton for entire dashboard */}
      {loading && (
        <Box>
          {/* Actions bar skeleton */}
          <Box 
            sx={(t) => ({ 
              mb: 2.5, 
              p: 1.5,
              borderRadius: `${t.ds.radius.md}px`,
              bgcolor: t.tokens.surface.sunken,
              border: `1px solid ${t.tokens.border.subtle}`,
            })}
          >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '8px' }} />
              <Skeleton variant="rectangular" width={180} height={36} sx={{ borderRadius: '8px' }} />
            </Box>
          </Box>
          {/* Widget skeletons */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(320px, 1fr))' }, gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Paper 
                key={i}
                sx={(t) => ({ 
                  p: 0, 
                  borderRadius: `${t.ds.radius.md}px`,
                  height: 300,
                  border: `1px solid ${t.tokens.border.subtle}`,
                  bgcolor: t.tokens.surface.raised,
                })}
              >
                <Box sx={(t) => ({ p: 1.5, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.25 }} />
                </Box>
                <Box sx={{ p: 1.5 }}>
                  <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: '6px' }} />
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
      
      
      <Box sx={{ ml: { xs: 0, md: '-10px' }, width: '100%', minWidth: 0 }}>
      {(!loading && widgets.length === 0) ? (
        <Paper 
          variant="outlined" 
          sx={(t) => ({ 
            p: 4, 
            textAlign: 'center',
            borderRadius: `${t.ds.radius.md}px`,
            border: `2px dashed ${t.tokens.border.default}`,
            bgcolor: t.tokens.surface.sunken,
            boxShadow: 'none'
          })}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1, 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1.125rem'
            }}
          >
            No widgets yet
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ maxWidth: 400, mx: 'auto', fontSize: '0.875rem' }}
          >
            Add a widget from a chat answer using "Add to Dashboard" to start building your dashboard.
          </Typography>
        </Paper>
      ) : (
        <Grid
          className="layout"
          layout={gridLayout as any}
          rowHeight={isMobile ? 68 : 80}
          cols={isMobile ? 1 : 12}
          onLayoutChange={onLayoutChangeSafe as any}
          isDraggable={!isMobile}
          isResizable={!isMobile}
          draggableHandle=".widget-drag-handle"
          onResize={onResize as any}
          onResizeStop={onResizeStop as any}
          measureBeforeMount
          useCSSTransforms
          compactType="vertical"
          margin={isMobile ? [8, 8] : [14, 14]}
        >
          {widgets.map((w) => (
            <div key={w.widget_id}>
              <Panel
                fill
                flush
                // A widget's controls are a few icons, so they share the title's
                // row: a toolbar row of their own took half a small tile.
                compact
                title={widgetTitle(w)}
                actions={widgetActions(w)}
                menu={widgetMenu(w)}
                loading={!!widgetLoading[w.widget_id]}
                expanded={
                  specs[w.widget_id]
                    ? {
                        title: getWidgetDisplayTitle(w),
                        content: widgetBody(w),
                        actions: expandedActions(w),
                      }
                    : undefined
                }
              >
                <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  {resizing?.id === w.widget_id && (
                    <Box
                      sx={(t) => ({
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        zIndex: 2,
                        px: 0.75,
                        py: 0.375,
                        borderRadius: `${t.ds.radius.sm}px`,
                        bgcolor: t.tokens.surface.inverse,
                        color: t.tokens.text.inverse,
                        ...t.typography.caption,
                        fontWeight: 600,
                        boxShadow: t.tokens.shadow.md,
                      })}
                    >
                      {resizing.w}×{resizing.h}
                    </Box>
                  )}
                  {widgetBody(w)}
                </Box>
              </Panel>
            </div>
          ))}
        </Grid>
      )}
      </Box>
      <OnboardingCoachmark
        open={showEditNudge}
        anchorRef={editAgentBtnRef}
        message={
          isNarrowCoach
            ? 'Tap the wand to chat with Copilot.'
            : 'Click to summarize or chat with Copilot about this widget.'
        }
        placement="top-end"
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirmDialog}
        onConfirm={confirmDelete}
        title="Delete widget"
        description="Are you sure you want to delete this widget? This action cannot be undone."
        confirmLabel="Delete"
        tone="destructive"
      />
      
      <AppDialog
        open={addWidgetOpen}
        onClose={closeAddWidgetDialog}
        size="sm"
        title="Ask a question"
        dividers
        primaryAction={{
          label: 'Open Copilot',
          onClick: () => {
            if (addWidgetQuestion.trim()) {
              navigate('/copilot', { state: { initialQuestion: addWidgetQuestion.trim() } });
              closeAddWidgetDialog();
            }
          },
          disabled: !addWidgetQuestion.trim(),
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: closeAddWidgetDialog }}
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Type your question below. You&apos;ll open Copilot with it filled in — press Enter there to run it, then you can add results to this dashboard from the conversation.
        </Alert>
        <TextField
          value={addWidgetQuestion}
          onChange={(e) => setAddWidgetQuestion(e.target.value)}
          label="Your question"
          placeholder="e.g., What are the top 10 products by revenue?"
          fullWidth
          multiline
          rows={3}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && addWidgetQuestion.trim()) {
              e.preventDefault();
              navigate('/copilot', { state: { initialQuestion: addWidgetQuestion.trim() } });
              closeAddWidgetDialog();
            }
          }}
        />
      </AppDialog>
    </Box>
  );

  return (
    <SidePanelDock
      open={chatOpen}
      storageKey={CHAT_WIDTH_KEY}
      label="Resize chat panel"
      panel={
        <DashboardChatPanel
          useStore={chatStore}
          dashboardName={dashboardName}
          contextChips={chatChips}
          onRemoveContextChip={(key) => setSelectedWidgetIds((ids) => ids.filter((id) => id !== key))}
          onSend={sendMessage}
          onStop={stopStream}
          onSQLApproval={sendSQLApproval}
          onAddToDashboard={pinToThisDashboard}
          onClose={() => setChatOpen(false)}
        />
      }
    >
      {page}
    </SidePanelDock>
  );
}

/**
 * A pinned query result, drawn exactly as the chat draws one: the same column
 * inference, the same formatting, the same paging straight from the frame.
 */
function WidgetTable({ spec, label }: { spec: any; label: string }) {
  const data = spec?.data || {};
  const rows: unknown[][] = data.preview_rows || [];
  const names: string[] = data.columns || [];
  const threadId: string | undefined = spec?.metadata?.thread_id;
  const frameId: string | undefined = data.frame_id;

  const columns = React.useMemo(
    () => buildResultColumns(names, rows, { columnTypes: data.column_types, columnSemantics: data.column_semantics }),
    [names, rows, data.column_types, data.column_semantics],
  );

  const onLoadPage =
    frameId && threadId ? (page: number, size: number) => fetchFramePage(frameId, threadId, page, size) : undefined;

  return (
    <ResultTable
      columns={columns}
      rows={rows}
      totalRows={data.row_count}
      onLoadPage={onLoadPage}
      fill
      ariaLabel={label}
    />
  );
}
